import db from '../db/db';
import { fileFromOfflineImage, uploadApi } from './api/upload';
import { isNetworkFailure } from './offlineFallback';

/**
 * Build the `newFiles` payload for `uploadApi.finding.update` out of offline
 * `images` rows.
 *
 * The rows keep the photo twice: the metadata (annotations, rotation, GPS) under
 * `sync.payload.file`, and the bytes as Base64 in `file`. Only the Base64 copy
 * survives on iOS — see `fileFromOfflineImage` — so the File is rebuilt from
 * that and overrides whatever stale `File` reference the payload still holds.
 * Rows with no readable data are dropped rather than uploaded empty, which the
 * API rejects with 400 "No file attached".
 */
export function findingFilesFromOfflineImages(images) {
  return (images || []).reduce((files, image) => {
    const file = fileFromOfflineImage(image);

    if (!file) {
      // eslint-disable-next-line no-console
      console.error(
        `Skipping finding photo ${image.guid}: no readable offline file data.`,
      );

      return files;
    }

    const meta = (image.sync && image.sync.payload && image.sync.payload.file) || {};

    files.push({
      ...meta,
      guid: image.guid,
      file,
      name: image.name || meta.name,
    });

    return files;
  }, []);
}

/**
 * SW2-2136 — upload this finding's queued photos, each batch as the type it was taken
 * as. Runs BEFORE a saved update is replayed: the update may be the one that closes
 * the finding, and the server refuses a close with no closure photo when the client
 * requires one. Photos used to go up after every replay, and all as 'initial'.
 *
 * Used by both sync branches — inspections already on the server and inspections created
 * offline — so the two cannot drift apart again.
 *
 * Safe to call more than once: only unsynced rows are sent, and each batch is marked
 * synced as it lands. The server deletes only uploads flagged `deleted`, so sending the
 * types in separate calls cannot drop the other batch.
 *
 * SW2-2443: a network failure is rethrown so the sync stops, instead of being logged
 * while every later request is fired at an unreachable server. A server rejection is
 * still logged and the photo left queued.
 */
export async function syncPendingFindingPhotos(finding, serverFindingId) {
  const pending = await db.images
    .where('payload_id')
    .equals(finding.id)
    // payload_id is shared with inspection attachments, which are keyed by
    // inspection id and queue their inspection_id; a finding id can equal one.
    .filter(
      (upload) =>
        !upload.sync.synced && upload.sync.payload?.inspection_id === undefined,
    )
    .toArray();

  if (!pending.length) return;

  const uploads = finding.finding_uploads
    ? finding.finding_uploads.map((upload) => upload)
    : [];
  const byType = pending.reduce((groups, image) => {
    const photoType =
      image.photo_type ||
      (image.sync && image.sync.payload && image.sync.payload.photo_type) ||
      'initial';

    (groups[photoType] = groups[photoType] || []).push(image);

    return groups;
  }, {});

  for (const [photoType, images] of Object.entries(byType)) {
    const newFiles = findingFilesFromOfflineImages(images);

    if (newFiles.length === 0) continue;

    // Per-finding: a photo the API rejects used to reject out of the whole
    // sync, so every later step was skipped. Only the rows that actually
    // went up are marked synced; anything skipped stays pending.
    try {
      await uploadApi.finding.update(serverFindingId, newFiles, uploads, photoType);
    } catch (err) {
      if (isNetworkFailure(err) || !navigator.onLine) throw err;

      // eslint-disable-next-line no-console
      console.error(`Failed to sync photos for finding ${finding.id}`, err);

      continue;
    }

    await db.images
      .where('guid')
      .anyOf(newFiles.map((f) => f.guid))
      .modify((img) => {
        img.sync.synced = true;
      });
  }
}
